import {createSelector} from 'reselect';

import {createAction, createReducer} from 'redux-act';

import {ENDPOINTS} from 'constants/endpoints';
import {LOCATION_CHANGED} from 'constants/actions';
import {ApplicationStoreType} from 'reducers';
import {getCountOfBoxes} from 'features/contacts/selectors/prescription/prescription';
import {getActiveProduct} from 'selectors/product/product';
import {getCoupon} from 'features/contacts/selectors/product/product';
import {instanceGusaBackend} from 'libs/api';

export type DiscountPriceType = {
    status: '' | 'fulfilled' | 'rejected' | 'pending';
    coupon: string;
    price: number;
    finalPrice: number;
};

export type FetchContactLensesDiscountPriceType = {
    quantity: number;
    coupon: string;
    productId: number;
};

export const initialState: DiscountPriceType = {
    status: '',
    coupon: '',
    price: 0,
    finalPrice: 0,
};

/**
 * Provides the discount price from backend for contact lenses
 */
export const fetchDiscountPrice = createAction(
    'GET_DISCOUNT_PRODUCT_PRICE_CONTACTS',
    (data: FetchContactLensesDiscountPriceType) => {
        return instanceGusaBackend({
            method: 'get',
            url: ENDPOINTS.PRODUCT_GET_DISCOUNT_PRICE({productId: data.productId}),
            params: {...data, version: 'v2.0'},
        });
    },
);

/**
 * Provides the calculated price via backend for contact lenses
 */
export const discountPrice = createReducer<DiscountPriceType>(
    {
        [`${fetchDiscountPrice}_FULFILLED`]: (state, payload): DiscountPriceType => ({
            ...state,
            coupon: payload.data.coupon,
            price: payload.data.price,
            finalPrice: payload.data.finalPrice,
            status: 'fulfilled',
        }),
        [`${fetchDiscountPrice}_PENDING`]: (state): DiscountPriceType => ({
            ...state,
            status: 'pending',
        }),
        [`${fetchDiscountPrice}_REJECTED`]: (state): DiscountPriceType => ({
            ...state,
            status: 'rejected',
        }),
        [LOCATION_CHANGED]: () => initialState,
    },
    initialState,
);

/**
 * Return discounted price
 *
 * @param {ApplicationStoreType} store - redux store
 * @returns {DiscountPriceType} discounted price
 */
export const getDiscountPrice = (store: ApplicationStoreType): DiscountPriceType =>
    store.contactsPdp.discountPrice;

/**
 * Provides the data needed to make a query to fetch price from backend for contact lenses
 * @returns {FetchContactLensesDiscountPriceType} - data
 */
export const getFetchContactLensesPriceData = createSelector(
    getActiveProduct,
    getCountOfBoxes,
    getCoupon,
    ({id}, quantity, coupon): FetchContactLensesDiscountPriceType => ({
        productId: Number(id),
        quantity,
        coupon,
    }),
);

/**
 * Returns the final prices for active product
 * in case, when price is not loaded from backend returns the initial prices
 */
export const getFinalPrice = createSelector(
    getDiscountPrice,
    getActiveProduct,
    getCountOfBoxes,
    (discount, activeProduct, countOfBoxes): DiscountPriceType => {
        return {
            ...discount,
            ...(discount.status === 'fulfilled'
                ? {
                      coupon: discount.coupon,
                      price: discount.price,
                      finalPrice: discount.finalPrice,
                  }
                : {
                      coupon: activeProduct.appliedCoupon,
                      price: activeProduct.price * (countOfBoxes || 1),
                      finalPrice: activeProduct.finalPrice * (countOfBoxes || 1),
                  }),
        };
    },
);

/**
 * Returns the final and default price per box based on calculated prices by backend
 * @returns {{finalPrice: number; price: number}} - prices
 */
export const getPricesPerBox = createSelector(
    getFinalPrice,
    getCountOfBoxes,
    (backendPrices, boxes): {finalPrice: number; price: number} => ({
        finalPrice: backendPrices.finalPrice / (boxes || 1),
        price: backendPrices.price / (boxes || 1),
    }),
);
